import { PlayerFactory } from './PlayerFactory';
import { LogicHandlers, PlayerEvent, StreamStatus } from './PlayerModel';
import { Lightning, Log, Registry, Router } from '@lightningjs/sdk';
import { PlayerUtil } from './PlayerUtil';
import { StreamManager } from './StreamManager';
import { UiEventHandlers } from './ui/UiEventHandlers';
import { BookmarkHandler } from './logicHandlers/BookmarkHandler';
import { PlayerControlsManager } from './PlayerControlsManager';
import { OverlayHelper } from './ui/OverlayHelper';
import { EpgEvent, StartPositionType, StreamInfo, StreamInfoType } from './streamInfo/StreamInfo';
import { UpdateScheduleHandler } from './logicHandlers/UpdateScheduleHandler';
import { LiveToReplayHandler } from './logicHandlers/LiveToReplayHandler';
import { SysCalls } from '../../syscalls/Syscalls';
import { ErrorTranslationKeys, PopupTranslationKeys } from '../../api/models/TranslationKeys';
import { WatchlistHandler } from './logicHandlers/WatchlistHandler';
import { AssetType, StreamType } from '../../api/models/Shared.types';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { Environment } from '../../config/environment';
import { AnalyticsHandler, ErrOriginator, PLAYER_STOP_REASON } from './logicHandlers/AnalyticsHandler';
import { PlayerService } from './PlayerService';
import { Backend } from '../../api/Backend';
import { LinearStreamInfo } from './streamInfo/LinearStreamInfo';
import { ChannelModel } from '../../api/EpgApi.types';
import { EpgUtil } from '../utility/EpgUtil';
import { EpgApi } from '../../api/EpgApi';
import { IProgram } from '../../api/models/AndromeTV.types';
import { hasReplayRights } from '../detail/epg/EpgWatchability';
import { LiveChannelHandler } from './logicHandlers/LiveChannelHandler';
import { PlayerUIState } from './ui/PlayerUIState';
import { CustomerSettingsKeys } from '../../api/models/CustomerSettingsKeys';
import { ContinueWatchingDialog } from '../../pages/dialogs/ContinueWatchingDialog';

export class PlayerController {
  playerService: PlayerService;
  uiEventHandlers: UiEventHandlers;
  seekEnabled: boolean;
  private _streaming: boolean;
  private _appRoot: Lightning.Component;
  private _backend: Backend;
  streamManager: StreamManager;
  controlsManager: PlayerControlsManager;
  overlayHelper: OverlayHelper;
  analyticsHandler: AnalyticsHandler;
  private _playerPauseRefreshTime: number;
  private _pauseTime: number | undefined;
  streamInfo: StreamInfo | undefined;
  preferredDrmProvider: string | undefined;
  private _logicHandlers: LogicHandlers | undefined;
  private firstInit: boolean;
  seekingSwitchStream = false;
  uiState: PlayerUIState;

  private liveChannelHandler: LiveChannelHandler;
  private sessionTimeTimeout: number | undefined;
  private continueWatchingTimeout: number | undefined;

  constructor(backend: Backend, appRoot: Lightning.Component) {
    this.playerService = PlayerFactory.createPlayerService();
    this.uiEventHandlers = new UiEventHandlers();
    this.seekEnabled = false;
    this._streaming = false;
    this._backend = backend;
    this._appRoot = appRoot;
    this.firstInit = false;
    this.firstInit = true;
    this.streamManager = new StreamManager(backend, this._keepaliveErrorHandler.bind(this));
    this.controlsManager = new PlayerControlsManager(backend, this.uiEventHandlers);
    this.overlayHelper = new OverlayHelper(backend);
    this.analyticsHandler = new AnalyticsHandler(backend);
    this._playerPauseRefreshTime = 25000;
    this._pauseTime = undefined;
    this.liveChannelHandler = new LiveChannelHandler(backend);
    this.uiState = {
      buffering: false,
      error: undefined,
      metadataLoaded: false,
    };
  }

  clearSessionTimeout() {
    if (this.sessionTimeTimeout) {
      Registry.clearTimeout(this.sessionTimeTimeout);
      this.sessionTimeTimeout = undefined;
    }
  }

  clearContinueWatchingTimeout() {
    if (this.continueWatchingTimeout) {
      Registry.clearTimeout(this.continueWatchingTimeout);
      this.continueWatchingTimeout = undefined;
    }
  }

  startStream(streamInfo: StreamInfo, updateUI = true, resetContinueWatching = true, isRestart = false) {
    // maybe change isRestart to Player stop reason
    if (this._streaming) {
      if (isRestart) {
        this.endStream(false, false, resetContinueWatching, PLAYER_STOP_REASON.RESTART);
      } else {
        this.endStream(false, false, resetContinueWatching, PLAYER_STOP_REASON.USER_INITIATED);
      }
    }
    this.streamInfo = streamInfo;
    this._preStartConfig(streamInfo);
    this._streaming = true;
    if (this.streamInfo?.isAdult) {
      // its adult: show error
      this._showError(this._backend.translations.forKey(ErrorTranslationKeys.error_adult_mode_required));
      return Promise.resolve();
    }
    return this.streamManager
      ?.start(streamInfo)
      .then(asset => {
        Log.info('init player', asset);
        streamInfo.currentAsset = asset;
        this.startContinueWatchingAndStreamTimeouts(resetContinueWatching);
        return this.setManifest(updateUI);
      })
      .catch(err => {
        this.analyticsHandler?.logPlayError(err, ErrOriginator.StartStream);
        this._showError(err.code);
        throw err;
      });
  }

  private restartStreamForSession() {
    // startstream ends stream aswel
    this.startStream(this.streamInfo, false, false, true);
  }

  public restartStream() {
    this.startStream(this.streamInfo, false, true, true);
  }

  private showContinueWatchingDialog() {
    // show dialog if no interaction then end stream, if there is interaction restart screen
    Router.getActivePage().fireAncestors('$openDialog', {
      type: ContinueWatchingDialog,
      confirmText: this._backend.translations.forKey(PopupTranslationKeys.popup_still_watching_btn_continue),
      message: this._backend.translations.forKey(PopupTranslationKeys.popup_still_watching_body),
      backend: this._backend,
      playerController: this,
    });
  }

  private startContinueWatchingAndStreamTimeouts(resetContinueWatching = true) {
    // only for linear streams
    if (this.streamInfo.streamType !== StreamType.LINEAR) return;

    const restartEnabled = this._backend.globalSettings.boolValue(GlobalSettingsKeys.restartLinearStreams);
    const sessionLifetime = this.streamInfo.currentAsset.sessionLifetime;
    const continueWatchingBeforeStreamEndTime =
      this._backend.globalSettings.numberValue(GlobalSettingsKeys.continueWatchingBeforeStreamEndTime) || 600000; // 10 minutes

    //  STB / TV platforms path / restartLinearStreams path
    if (restartEnabled) {
      let maxLinearStreamOption = 0;
      this._backend.cachedCustomerSettings
        .getSetting(this._backend, CustomerSettingsKeys.max_linear_stream_time)
        .then(r => {
          if (r) {
            maxLinearStreamOption = +r.value * 1000; // seconds to ms
          } else {
            maxLinearStreamOption = this._backend.globalSettings.numberValue(
              GlobalSettingsKeys.defaultMaxLinearStreamTime
            ); // already ms
          }

          if (maxLinearStreamOption > sessionLifetime) {
            const restartFraction = this._backend.globalSettings.numberValue(
              GlobalSettingsKeys.restartLinearStreamFraction
            );
            const sessionTime = restartFraction * sessionLifetime;
            this.sessionTimeTimeout = Registry.setTimeout(() => {
              this.restartStreamForSession();
            }, sessionTime);
          }

          const continueWatchingTime = maxLinearStreamOption - continueWatchingBeforeStreamEndTime;
          if (resetContinueWatching) {
            // old timeout cleared by endstream
            this.continueWatchingTimeout = Registry.setTimeout(() => {
              this.showContinueWatchingDialog();
            }, continueWatchingTime);
          }
        });
    } else {
      // OTT path / no restartLinearStreams
      const continueWatchingTime = sessionLifetime - continueWatchingBeforeStreamEndTime;

      if (resetContinueWatching) {
        // old timeout cleared by endstream
        this.continueWatchingTimeout = Registry.setTimeout(() => {
          this.showContinueWatchingDialog();
        }, continueWatchingTime);
      }
    }
  }

  startRecentStream() {
    if (!this._streaming) {
      if (this.streamInfo?.streamType !== StreamType.LINEAR) {
        this.streamInfo?.updateStartPositionType(StartPositionType.BOOKMARK);
      }
      if (this._backend && this.streamInfo && this.streamInfo.streamType !== StreamType.LINEAR) {
        this.startStream(this.streamInfo);
      } else {
        this.fallbackToLinearStream();
      }
    }
  }

  fallbackToLinearStream() {
    if (SysCalls.isOnline) {
      return this.liveChannelHandler.init().then(() => {
        const streamInfo = this.liveChannelHandler.streamInfo;
        if (streamInfo) {
          return this.startStream(streamInfo);
        }
      });
    } else {
      this.streamInfo = this.liveChannelHandler.streamInfo;
      return Promise.resolve();
    }
  }

  setManifest(updateUI = true) {
    if (!this._backend) {
      return Promise.resolve();
    }
    return this.streamManager
      ?.getManifest()
      .then(result => {
        if (!this._backend || !result) return;
        this.playerService
          .start(
            this._backend,
            result,
            PlayerUtil.getInitialBitrate(this._backend.globalSettings),
            this.streamInfo?.startPositionTime
          )
          .then(() => {
            if (this.streamInfo) this._onStartConfig(this.streamInfo, updateUI);
          });
      })
      .catch(err => {
        this._showError(err.code);
      });
  }

  get isStreaming() {
    return this._streaming;
  }

  changeStream(streamInfo: StreamInfo, updateUI = true, resetSeekbar = true) {
    if (this._streaming) this.endStream(false);
    if (resetSeekbar) this.controlsManager?.resetSeekbar();
    if (!this._backend) return Promise.resolve();
    return this.startStream(streamInfo, updateUI);
  }

  _preStartConfig(streamInfo: StreamInfo) {
    this._playerPauseRefreshTime =
      this._backend?.globalSettings.numberValue(GlobalSettingsKeys.playerPauseStopPlayerTime) || 25000;
    this.preferredDrmProvider =
      this._backend?.globalSettings.stringValue(GlobalSettingsKeys.preferredDrmProvider) ||
      Environment.Castlabs.defaultDrmProvider;
    this.seekEnabled = streamInfo.allowSeek;
    this.resetUiState();
    this.uiEventHandlers.updateTitleBarHandler(streamInfo);
    this._createLogicHandlers(streamInfo);
    this.streamInfo = streamInfo;
    if (streamInfo.streamType === StreamType.LINEAR && streamInfo.channel?.channelId) {
      this.liveChannelHandler.storeChannelId(streamInfo.channel.channelId);
    }
  }

  _onStartConfig(streamInfo: StreamInfo, updateUI = true) {
    this.analyticsHandler?.logPlayStart();
    if (updateUI) this.overlayHelper?.onStart();
    this._logicHandlersStart();
    this._addEventListeners();
    if (this.controlsManager) this.controlsManager.streamInfo = streamInfo;
  }

  /**
   * Ends current playback
   */
  endStream(error = false, restartLinear = false, clearContinueWatching = true, stopReason?: PLAYER_STOP_REASON) {
    if (!error) {
      this.analyticsHandler?.logPlayStop(stopReason);
    }
    this.clearSessionTimeout();
    if (clearContinueWatching) {
      this.clearContinueWatchingTimeout();
    }
    this.cleanup();
    if (restartLinear) {
      this.fallbackToLinearStream();
    }
  }

  // channelUp() {
  //   if (!(this.streamInfo instanceof LinearStreamInfo)) return;
  //
  //   this.validChannelsSortedByChannelNumber().then(sortedChannels => {
  //     if (!(this.streamInfo instanceof LinearStreamInfo)) return;
  //     const linearStream = this.streamInfo as LinearStreamInfo;
  //     const index = sortedChannels.findIndex(el => linearStream._epgEvent.channel.channelId === el.channelId);
  //     // find current channel +1 and start stream;
  //     let channel: ChannelModel | undefined;
  //     if (index + 1 < sortedChannels.length) {
  //       channel = sortedChannels[index + 1];
  //     } else {
  //       channel = sortedChannels[0];
  //     }
  //
  //     if (!channel) return;
  //     this.playLiveChannel(channel, false);
  //   });
  // }

  // channelDown() {
  //   if (!(this.streamInfo instanceof LinearStreamInfo)) return;
  //
  //   this.validChannelsSortedByChannelNumber().then(sortedChannels => {
  //     if (!(this.streamInfo instanceof LinearStreamInfo)) return;
  //     const linearStream = this.streamInfo as LinearStreamInfo;
  //     const index = sortedChannels.findIndex(el => linearStream._epgEvent.channel.channelId === el.channelId);
  //     // find current channel -1 and start stream;
  //     let channel: ChannelModel | undefined;
  //     if (index - 1 >= 0) {
  //       channel = sortedChannels[index - 1];
  //     } else {
  //       channel = sortedChannels[sortedChannels.length - 1];
  //     }
  //
  //     if (!channel) return;
  //     this.playLiveChannel(channel, false);
  //   });
  // }

  removeUIHandlers() {
    this.uiEventHandlers = new UiEventHandlers();
  }

  playChannelNumber(chNum: number) {
    if (!(this.streamInfo instanceof LinearStreamInfo)) return;

    this.validChannelsSortedByChannelNumber().then(sortedChannels => {
      // find channel closest to input number
      const chNumbs = sortedChannels.map(el => el.channelNumber || 999);
      const closest = chNumbs.reduce((prev, curr) => {
        return Math.abs(curr - chNum) < Math.abs(prev - chNum) ? curr : prev;
      });
      const index = chNumbs.findIndex(el => closest === el);
      const channel: ChannelModel | undefined = sortedChannels[index];

      if (!channel) return;
      this.playLiveChannel(channel, true);
    });
  }

  validChannelsSortedByChannelNumber(): Promise<ChannelModel[]> {
    const catalogID = this._backend.deviceProfile?.profile.catalog;

    function compare(a: ChannelModel, b: ChannelModel): number {
      const chA = a.catalogs?.find(cat => cat.catalogId === catalogID)?.channelNumber;
      a.channelNumber = chA;
      const chB = b.catalogs?.find(cat => cat.catalogId === catalogID)?.channelNumber;
      b.channelNumber = chB;
      if (chA && chB) {
        if (chA < chB) {
          return -1;
        }
        if (chA > chB) {
          return 1;
        }
      }
      return 0;
    }

    return this._backend.cachedChannelApi.validChannels(this._backend, false).then(r => {
      const validChannels = r.validChannels;
      const sortedChannels = validChannels.sort(compare);
      return sortedChannels;
    });
  }

  playLiveChannel(channel: ChannelModel, updateUI = true) {
    const backend = this._backend;

    const fromUntil = EpgUtil.epgFromUntil(backend);

    EpgApi.scheduleForChannel(backend, channel.channelId, fromUntil.from, fromUntil.until).then(schedules => {
      const currentSchedule = EpgUtil.findCurrentSchedule(schedules.schedules);
      if (!channel) {
        return;
      }

      const replay = hasReplayRights(currentSchedule, channel);
      let getProgramPromise: Promise<IProgram | undefined>;
      if (currentSchedule) {
        getProgramPromise = EpgApi.program(
          backend,
          currentSchedule.program,
          fromUntil.from,
          fromUntil.until,
          backend.deviceProfile?.profile.catalog
        );
      } else {
        getProgramPromise = Promise.resolve(undefined);
      }

      getProgramPromise.then(program => {
        if (!channel) {
          return;
        }

        const epgEvent: EpgEvent = {
          channel: channel,
          program: program?.program,
          series: program?.series,
          schedule: currentSchedule,
          recording: undefined,
        };

        const streamInfo = new LinearStreamInfo(epgEvent, backend, replay);
        this.changeStream(streamInfo, updateUI);
      });
    });
  }

  cleanup(deleteStream = true) {
    if (deleteStream) {
      this.streamManager?.end().catch(err => {
        this.analyticsHandler?.logPlayError(err, ErrOriginator.StopStream);
      });
    }
    this.controlsManager?.stop();
    this._logicHandlersStop();
    this.playerService.close();
    this._removeEventListeners();
    this._streaming = false;
  }

  /**
   * @param {string} code
   * @private
   */
  _showError(code: string | undefined = undefined) {
    let errMsg: string;
    if (code) {
      errMsg = this._backend?.translations.forKey(code) || '';
      errMsg = !errMsg.startsWith('*')
        ? errMsg
        : this._backend?.translations.forKey(ErrorTranslationKeys.error_streaming) || 'undefined';
    } else {
      if (!SysCalls.isOnline) {
        errMsg = this._backend?.translations.forKey(ErrorTranslationKeys.error_connectivity_lost) || 'undefined';
      } else {
        errMsg = this._backend?.translations.forKey(ErrorTranslationKeys.error_streaming) || 'undefined';
      }
    }
    this.uiState.error = errMsg;
    this.uiEventHandlers.errorHandler(errMsg);
    this.overlayHelper?.handleError();
    this.streamManager?.setStreamStatus(StreamStatus.IDLE);
    this.endStream(true);
  }

  /**
   * @param event
   * @private
   */
  _onPlayerPlay(event: any) {
    Log.info('player is playing', event);
    this.streamManager?.setStreamStatus(StreamStatus.PLAYING);
    this.uiEventHandlers.spinnerHandler(false);
    SysCalls.setScreensaver(false);
  }

  play() {
    const pauseTime = this._pauseTime;
    this._pauseTime = undefined;
    if (this._logicHandlers?.liveToReplayHandler) {
      // If watching live, switch to replay stream
      this._logicHandlers.liveToReplayHandler.resume();
    } else if (pauseTime && Date.now() - pauseTime >= (this._playerPauseRefreshTime || 0)) {
      // If paused for long time, reset player and reload manifest and start from bookmark
      // TODO: maybe give stop reason restart along
      this.cleanup(true);
      this.streamInfo.updateStartPositionType(StartPositionType.BOOKMARK);
      this.startStream(this.streamInfo);
    } else {
      this.playerService.play();
    }
  }

  /**
   * @param event
   * @private
   */
  _onPlayerPause(event: any) {
    Log.info('player is paused', event);
    this.streamManager?.setStreamStatus(StreamStatus.PAUSED);
    if (this._logicHandlers?.liveToReplayHandler) {
      this._logicHandlers.liveToReplayHandler.pause();
    }
    if (this._logicHandlers?.watchlistHandler) {
      this._logicHandlers.watchlistHandler.stop();
    }
    SysCalls.setScreensaver(true);
    this._pauseTime = Date.now();
  }

  /**
   * @param event
   * @private
   */
  _onPlayerError(event: any) {
    Log.info('player error', event);
    this.analyticsHandler?.logPlayError(event, ErrOriginator.Player);
    this._showError();
  }

  /**
   * @param event
   * @private
   */
  _onPlayerEnd(event: any) {
    Log.info('player is ended', event);
    this.streamManager?.setStreamStatus(StreamStatus.IDLE);
    this.endStream(false, true);
    Router.navigate('main');
  }

  _onPlayerBuffering(event: any) {
    Log.info('player is buffering', event);
    if (!this.controlsManager?.isTrickPlay) {
      this.uiState.buffering = true;
      this.uiEventHandlers.spinnerHandler(this.uiState.buffering);
    }
  }

  _onPlayerEndBuffering(event: any) {
    Log.info('player ended buffering', event);
    if (!this.controlsManager?.isTrickPlay) {
      this.uiState.buffering = false;
      this.uiEventHandlers.spinnerHandler(this.uiState.buffering);
    }
  }

  _onPlayerLoadedMeta(event: any) {
    Log.info('player loaded metadata', event);
    this.uiState.metadataLoaded = true;
    this.controlsManager?.setInitialButtonConfig(this.uiState.metadataLoaded);
    this.controlsManager?.start();
  }

  onSeek() {
    if (this._logicHandlers?.liveToReplayHandler) {
      this._logicHandlers.liveToReplayHandler.onSeek();
      this.seekingSwitchStream = true;
      return true;
    }
    return false;
  }

  onTrickPlay() {
    if (this._logicHandlers?.liveToReplayHandler) {
      return this._logicHandlers.liveToReplayHandler.onTrickPlay();
    }
    return Promise.resolve();
  }

  /**
   * @private
   */
  _addEventListeners() {
    this.playerService.on(PlayerEvent.PLAYING, this._onPlayerPlay.bind(this));
    this.playerService.on(PlayerEvent.PAUSE, this._onPlayerPause.bind(this));
    this.playerService.on(PlayerEvent.ERROR, this._onPlayerError.bind(this));
    this.playerService.on(PlayerEvent.ENDED, this._onPlayerEnd.bind(this));
    this.playerService.on(PlayerEvent.BUFFERING_STARTED, this._onPlayerBuffering.bind(this));
    this.playerService.on(PlayerEvent.LOADEDMETADATA, this._onPlayerLoadedMeta.bind(this));
    this.playerService.on(PlayerEvent.BUFFERING_ENDED, this._onPlayerEndBuffering.bind(this));
  }

  /**
   * @private
   */
  _removeEventListeners() {
    this.playerService.removeEventListeners();
  }

  /**
   * @param err
   * @private
   */
  _keepaliveErrorHandler(err: any) {
    this.analyticsHandler?.logKeepAliveError(err);
    this._showError(err.code);
  }

  _createLogicHandlers(streamInfo: StreamInfo) {
    this._logicHandlers = {
      scheduleHandler: undefined,
      watchlistHandler: undefined,
      liveToReplayHandler: undefined,
      bookmarkHandler: undefined,
    };
    if (streamInfo.type === StreamInfoType.LINEAR) {
      const linearStream = streamInfo as LinearStreamInfo;
      this._logicHandlers.scheduleHandler = new UpdateScheduleHandler(
        this._backend,
        linearStream,
        this.uiEventHandlers
      );
      if (linearStream.assetId)
        this._logicHandlers.watchlistHandler = new WatchlistHandler(
          this._backend,
          linearStream.assetId,
          AssetType.CHANNEL
        );
      if (linearStream.allowSeek) {
        this._logicHandlers.liveToReplayHandler = new LiveToReplayHandler(this._backend, linearStream);
      }
    } else {
      this._logicHandlers.bookmarkHandler = new BookmarkHandler(this._backend, streamInfo);
    }
  }

  _logicHandlersStop() {
    if (this._logicHandlers?.bookmarkHandler) {
      this._logicHandlers.bookmarkHandler.stop();
    }
    if (this._logicHandlers?.scheduleHandler) {
      this._logicHandlers.scheduleHandler.stop();
    }
    if (this._logicHandlers?.watchlistHandler) {
      this._logicHandlers.watchlistHandler.stop();
    }
  }

  _logicHandlersStart() {
    if (this._logicHandlers?.bookmarkHandler) {
      this._logicHandlers.bookmarkHandler.start();
    }
    if (this._logicHandlers?.scheduleHandler) {
      this._logicHandlers.scheduleHandler.start();
    }
    if (this._logicHandlers?.watchlistHandler) {
      this._logicHandlers.watchlistHandler.start();
    }
  }

  onUiAttached() {
    this.uiEventHandlers.spinnerHandler(this.uiState.buffering);
    this.uiEventHandlers.errorHandler(this.uiState.error);
    if (this.uiState.error) {
      this.overlayHelper?.handleError();
    } else {
      if (this.streamInfo) {
        this.uiEventHandlers.updateTitleBarHandler(this.streamInfo);
      }
      this.controlsManager?.setInitialButtonConfig(this.uiState.metadataLoaded);
      this.overlayHelper.onStart();
    }
  }

  private resetUiState() {
    this.uiState = {
      buffering: false,
      metadataLoaded: false,
      error: undefined,
    };
    this.uiEventHandlers.errorHandler(undefined);
  }

  get isInErrorState() {
    return !!this.uiState.error;
  }
}
